import { Constants } from "./constants";

export interface IHerdingDeductionExercise {
  exercise: string;
  points: number;
}

// Course A's designated tie-break obstacle (see AkcHerdingHighInTrialContextBuilder):
// when two dogs are tied on score, the higher percentage of points earned here wins.
const CENTER_LINE_GATE_EXERCISE: IHerdingDeductionExercise = {
  exercise: "Center-Line Gate",
  points: 20,
};

export const HERDING_COURSE_A_TIE_BREAK_EXERCISE = CENTER_LINE_GATE_EXERCISE.exercise;

// Course A: same 6 obstacles regardless of level.
const COURSE_A_EXERCISES: IHerdingDeductionExercise[] = [
  { exercise: "Outrun/Lift Fetch", points: 20 },
  { exercise: `"Y" Chute`, points: 15 },
  { exercise: `"Z" Chute`, points: 15 },
  { exercise: "Hold/Exam Pen or Panel Runway in Started", points: 15 },
  CENTER_LINE_GATE_EXERCISE,
  { exercise: "Pen", points: 15 },
];

// Course B: items 1-5 are the same at every level; item 6 ("Hold") splits
// into "Shed" + "Hold" at Advanced.
const COURSE_B_COMMON_EXERCISES: IHerdingDeductionExercise[] = [
  { exercise: "Outrun", points: 20 },
  { exercise: "Lift", points: 10 },
  { exercise: "Fetch", points: 20 },
  { exercise: "Drive (Fetch II in Started)", points: 30 },
  { exercise: "Pen", points: 10 },
];

// Course C: items 1-4 are the same at every level. "Grazes" changes point
// value by level, and "Placement" only applies at Intermediate/Advanced.
const COURSE_C_COMMON_EXERCISES: IHerdingDeductionExercise[] = [
  { exercise: "Exit from Pen and Re-Run", points: 15 },
  { exercise: "Narrow Road", points: 20 },
  { exercise: "Bridge", points: 10 },
  { exercise: "Pause/Traffic", points: 20 },
];

/**
 * AKC Herding official scoresheet exercises, keyed by course
 * (Constants.dog_class_element.course_a/b/c) then level
 * (Constants.dog_class_element_level.started/intermediate/advance).
 *
 * Course D is not included here - see HERDING_COURSE_D_ELEMENTS and
 * HERDING_COURSE_D_OPTIONAL_ELEMENT_COUNT, since it uses a different
 * "general elements + optional elements" structure instead of a fixed,
 * numbered obstacle list.
 */
export const HerdingDeductions: Record<
  string,
  Record<string, IHerdingDeductionExercise[]>
> = {
  [Constants.dog_class_element.course_a]: {
    [Constants.dog_class_element_level.started]: COURSE_A_EXERCISES,
    [Constants.dog_class_element_level.intermediate]: COURSE_A_EXERCISES,
    [Constants.dog_class_element_level.advance]: COURSE_A_EXERCISES,
  },
  [Constants.dog_class_element.course_b]: {
    [Constants.dog_class_element_level.started]: [
      ...COURSE_B_COMMON_EXERCISES,
      { exercise: "Hold", points: 10 },
    ],
    [Constants.dog_class_element_level.intermediate]: [
      ...COURSE_B_COMMON_EXERCISES,
      { exercise: "Hold", points: 10 },
    ],
    [Constants.dog_class_element_level.advance]: [
      ...COURSE_B_COMMON_EXERCISES,
      { exercise: "Shed", points: 5 },
      { exercise: "Hold", points: 5 },
    ],
  },
  [Constants.dog_class_element.course_c]: {
    [Constants.dog_class_element_level.started]: [
      ...COURSE_C_COMMON_EXERCISES,
      { exercise: "Grazes", points: 35 },
    ],
    [Constants.dog_class_element_level.intermediate]: [
      ...COURSE_C_COMMON_EXERCISES,
      { exercise: "Grazes", points: 25 },
      { exercise: "Placement", points: 10 },
    ],
    [Constants.dog_class_element_level.advance]: [
      ...COURSE_C_COMMON_EXERCISES,
      { exercise: "Grazes", points: 25 },
      { exercise: "Placement", points: 10 },
    ],
  },
};

/**
 * Course D's fixed "General Elements" - same for every level. Unlike
 * Courses A/B/C, Course D also has freeform "Optional Elements" the
 * club/judge define per trial (see HERDING_COURSE_D_OPTIONAL_ELEMENT_COUNT).
 */
export const HERDING_COURSE_D_ELEMENTS: IHerdingDeductionExercise[] = [
  { exercise: "Take Pen", points: 10 },
  { exercise: "Drive/Fetch", points: 20 },
  { exercise: "Outrun", points: 8 },
  { exercise: "Lift", points: 4 },
  { exercise: "Fetch", points: 8 },
  { exercise: "Freestanding Obstacle", points: 10 },
  { exercise: "Exhaust Pen", points: 10 },
];

export const HERDING_COURSE_D_OPTIONAL_ELEMENT_COUNT = 3;
export const HERDING_COURSE_D_OPTIONAL_ELEMENT_POINTS = 10;
